import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { recordFullCrosswordCompletion } from '../../firebase/fullCrosswordService';
import {
  parseFullCrosswordPuzzle,
  isBlackCell,
  isCompleteAndCorrect,
  fullCrosswordScoreFromSeconds,
} from './fullCrosswordUtils';
import CozyPlayShell from '../../components/CozyPlayShell/CozyPlayShell';
import { HowToPlay } from './HowToPlay';
import './FullCrossword.css';

const PUZZLE_PATH = `${process.env.PUBLIC_URL || ''}/puzzles/full_crossword_sample.json`;
const HOW_TO_KEY = 'full_crossword_how_to_play_dismissed_v1';
const DAILY_BASE = '/full_crossword/v1';
const DAILY_CACHE_PREFIX = 'full_crossword_daily_cache_';
const TOMORROW_KEY_PREFIX = 'full_crossword_tomorrow_msg_';

function utcDateId(d = new Date()) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dailyCacheKey(dateId) {
  return `${DAILY_CACHE_PREFIX}${dateId}`;
}

function tomorrowKey(dateId) {
  return `${TOMORROW_KEY_PREFIX}${dateId}`;
}

function progressStorageKey(puzzleId) {
  return `full_crossword_progress_${puzzleId}`;
}

function buildEmptyUserGrid(puzzle) {
  return puzzle.solution.map((row, r) =>
    row.map((_, c) => (isBlackCell(puzzle, r, c) ? '.' : ''))
  );
}

export default function FullCrossword() {
  const { user, refreshProfile } = useAuth();
  const [puzzle, setPuzzle] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [userGrid, setUserGrid] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [timerOn, setTimerOn] = useState(false);
  const [won, setWon] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);
  const saveDebounceRef = useRef(null);
  const inputRefs = useRef({});
  const elapsedRef = useRef(0);
  const [tomorrowMsg, setTomorrowMsg] = useState(null);
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dateId = utcDateId();
        const dailyUrl = `${DAILY_BASE}/${dateId}.json`;

        let raw = null;
        try {
          const cached = window.localStorage.getItem(dailyCacheKey(dateId));
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && parsed.payload) raw = parsed.payload;
          }
        } catch {
          /* ignore */
        }

        if (!raw) {
          try {
            const res = await fetch(dailyUrl, { cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            raw = await res.json();
            try {
              window.localStorage.setItem(dailyCacheKey(dateId), JSON.stringify({ at: Date.now(), payload: raw }));
            } catch {
              /* ignore */
            }
          } catch {
            // fallback to bundled sample
            const res = await fetch(PUZZLE_PATH);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            raw = await res.json();
          }
        }

        const p = parseFullCrosswordPuzzle(raw);
        // Ensure puzzleId is stable for daily puzzles
        if (p?.id && /^ws_\\d{4}-\\d{2}-\\d{2}$/.test(p.id)) {
          // already stable
        }

        let grid = buildEmptyUserGrid(p);
        let startElapsed = 0;
        try {
          const saved = window.localStorage.getItem(progressStorageKey(p.id));
          if (saved) {
            const { elapsed: e, grid: g } = JSON.parse(saved);
            if (typeof e === 'number') startElapsed = e;
            if (Array.isArray(g) && g.length === p.rows) {
              grid = p.solution.map((row, r) =>
                row.map((_, c) => {
                  if (isBlackCell(p, r, c)) return '.';
                  const ch = (g[r] && g[r][c]) || '';
                  if (ch === '.' || ch === '') return '';
                  return String(ch).toUpperCase().slice(0, 1);
                })
              );
            }
          }
        } catch {
          /* ignore */
        }
        if (!cancelled) {
          setPuzzle(p);
          setUserGrid(grid);
          setElapsed(startElapsed);
          elapsedRef.current = startElapsed;
          setTomorrowMsg(null);
        }
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'Could not load puzzle');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(HOW_TO_KEY) === '1') return;
    setShowHowToPlay(true);
  }, []);

  const handleHowToPlayClose = () => {
    setShowHowToPlay(false);
    try {
      window.localStorage.setItem(HOW_TO_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    elapsedRef.current = elapsed;
  }, [elapsed]);

  useEffect(() => {
    if (!timerOn || won) return undefined;
    timerRef.current = window.setInterval(() => {
      setElapsed((x) => {
        const n = x + 1;
        elapsedRef.current = n;
        return n;
      });
    }, 1000);
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timerOn, won]);

  const persistProgress = useCallback(
    (p, grid, el) => {
      if (!p) return;
      try {
        localStorage.setItem(
          progressStorageKey(p.id),
          JSON.stringify({ elapsed: el, grid })
        );
      } catch {
        /* ignore */
      }
    },
    []
  );

  const clearSavedProgress = useCallback((puzzleId) => {
    try {
      localStorage.removeItem(progressStorageKey(puzzleId));
    } catch {
      /* ignore */
    }
  }, []);

  const focusCell = (r, c) => {
    const key = `${r},${c}`;
    const el = inputRefs.current[key];
    if (el) el.focus();
  };

  const handleCellChange = (r, c, value, p) => {
    if (won || isBlackCell(p, r, c)) return;
    const letter = value.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 1);
    const nextGrid = userGrid.map((row) => [...row]);
    nextGrid[r][c] = letter;
    setUserGrid(nextGrid);

    if (!timerOn && letter) {
      setTimerOn(true);
    }

    if (saveDebounceRef.current) window.clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = window.setTimeout(() => {
      persistProgress(p, nextGrid, elapsedRef.current);
    }, 250);

    if (isCompleteAndCorrect(p, nextGrid)) {
      if (saveDebounceRef.current) {
        window.clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = null;
      }
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setWon(true);
      setTimerOn(false);
      clearSavedProgress(p.id);

      try {
        const dateId = utcDateId();
        if (window.localStorage.getItem(tomorrowKey(dateId)) !== '1') {
          window.localStorage.setItem(tomorrowKey(dateId), '1');
          setTomorrowMsg('Come back tomorrow for a new puzzle.');
        }
      } catch {
        setTomorrowMsg('Come back tomorrow for a new puzzle.');
      }
    } else if (letter) {
      let nr = r;
      let nc = c + 1;
      while (nr < p.rows && (nc >= p.cols || isBlackCell(p, nr, nc))) {
        if (nc >= p.cols) {
          nc = 0;
          nr += 1;
        }
        if (nr < p.rows && isBlackCell(p, nr, nc)) nc += 1;
      }
      if (nr < p.rows && !isBlackCell(p, nr, nc)) {
        window.requestAnimationFrame(() => focusCell(nr, nc));
      }
    }
  };

  const handleKeyDown = (e, r, c, p) => {
    if (e.key === 'Backspace' && !userGrid[r][c]) {
      let pr = r;
      let pc = c - 1;
      while (pr >= 0 && (pc < 0 || isBlackCell(p, pr, pc))) {
        if (pc < 0) {
          pc = p.cols - 1;
          pr -= 1;
        }
        if (pr >= 0 && isBlackCell(p, pr, pc)) pc -= 1;
      }
      if (pr >= 0 && !isBlackCell(p, pr, pc)) {
        e.preventDefault();
        focusCell(pr, pc);
      }
    }
  };

  const handleSaveScore = async () => {
    if (!puzzle || !won) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const score = fullCrosswordScoreFromSeconds(elapsed);
      const result = await recordFullCrosswordCompletion(score, puzzle.id);
      if (result.ok) {
        setSaveMsg(
          result.improved
            ? `Saved! New best score (${result.score}).`
            : `Saved! Score ${result.score} (best unchanged).`
        );
        await refreshProfile();
      } else if (result.reason === 'auth') {
        setSaveMsg('Sign in to save scores and streaks.');
      } else {
        setSaveMsg('Could not save right now. Try again when you are online.');
      }
    } catch (err) {
      setSaveMsg(err?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return <p className="full-crossword__error">{loadError}</p>;
  }
  if (!puzzle || !userGrid) {
    return <p className="full-crossword__meta">Loading puzzle…</p>;
  }

  const p = puzzle;
  const score = fullCrosswordScoreFromSeconds(elapsed);

  return (
    <CozyPlayShell
      className="full-crossword"
      title="Word Square"
      showModeTabs={false}
      onHowToPlay={() => setShowHowToPlay(true)}
      subtitle={p.title}
      meta={
        <>
          <span>{won ? `Solved in ${elapsed}s · score ${score}` : `Time: ${elapsed}s`}</span>
          {won && tomorrowMsg ? <span>{tomorrowMsg}</span> : null}
        </>
      }
    >
      {showHowToPlay && <HowToPlay onClose={handleHowToPlayClose} />}

      <div className="full-crossword__grid">
        {p.solution.map((row, r) => (
          <div key={r} className="full-crossword__row">
            {row.map((_, c) => {
              if (isBlackCell(p, r, c)) {
                return <div key={c} className="full-crossword__cell full-crossword__cell--black" aria-hidden />;
              }
              const key = `${r},${c}`;
              return (
                <input
                  key={key}
                  ref={(el) => {
                    inputRefs.current[key] = el;
                  }}
                  className="full-crossword__cell"
                  maxLength={1}
                  value={userGrid[r][c]}
                  disabled={won}
                  aria-label={`Row ${r + 1} column ${c + 1}`}
                  onChange={(e) => handleCellChange(r, c, e.target.value, p)}
                  onKeyDown={(e) => handleKeyDown(e, r, c, p)}
                />
              );
            })}
          </div>
        ))}
      </div>

      <div className="full-crossword__clues">
        <h3>Rows</h3>
        <ol>
          {(p.rowClues?.length ? p.rowClues : p.acrossClues).map((clue, i) => (
            <li key={`r${i}`}>{clue}</li>
          ))}
        </ol>
        <h3>Columns</h3>
        <ol>
          {(p.colClues?.length ? p.colClues : p.downClues).map((clue, i) => (
            <li key={`c${i}`}>{clue}</li>
          ))}
        </ol>
        {p.license?.source && (
          <p className="full-crossword__license">
            Definitions source: {p.license.source} ({p.license.spdx || 'see source'}).{' '}
            <a
              href="https://en.wiktionary.org/wiki/Wiktionary:CC_BY-SA_3.0"
              target="_blank"
              rel="noreferrer"
            >
              License details
            </a>
          </p>
        )}
      </div>

      <div className="full-crossword__actions">
        <button
          type="button"
          className="full-crossword__btn full-crossword__btn--primary"
          disabled={!won || saving}
          onClick={handleSaveScore}
        >
          Save score &amp; streak
        </button>
        {!user && (
          <p className="full-crossword__msg">
            <Link to="/login">Sign in</Link> to save your best score to the leaderboard.
          </p>
        )}
        {saveMsg && <p className="full-crossword__msg">{saveMsg}</p>}
      </div>
    </CozyPlayShell>
  );
}
